'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';

import { bookmarkArticle, getArticleBookmarkStatus, getBookmarkList } from '../apis/profile/bookmark';
import { onBookmarkArticle } from '../helpers/events';
import type { BookmarkChip, BookmarkItemType } from '../types/articles';
import type { AuthorType } from '../types/shared';

export const useArticleBookmarks = ({
  initialList,
  onSuccess,
  enabled,
}: {
  initialList?: BookmarkItemType[];
  onSuccess?: (data: BookmarkChip[]) => void;
  enabled?: boolean;
}) => {
  const { user } = useAuth();

  return useQuery(
    ['bookmarkList'],
    () =>
      getBookmarkList(user?.profile.sub as string, user?.access_token as string).then((result) => {
        return result.data;
      }),
    {
      enabled,
      initialData: initialList,
      onSuccess,
    },
  );
};

export const useBookmarkArticle = (articleId: string, authors?: AuthorType[]) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation(
    ['bookmarkArticle'],
    (args: { listIds: string[]; articleId: string }) => bookmarkArticle(args, user?.access_token as string),
    {
      onSettled: () => {
        queryClient.refetchQueries(['bookmarkStatus']);
        queryClient.refetchQueries([`articleBookmarksCount: ${articleId}`]);
      },
      onSuccess: async () => {
        if (authors) {
          const previousBookmarkData = queryClient.getQueryData(['bookmarkStatus', articleId]) as string[];
          await queryClient.refetchQueries(['bookmarkStatus', articleId]);
          const newBookmarkData = queryClient.getQueryData(['bookmarkStatus', articleId]) as string[];
          const isBookmarkAdded = newBookmarkData?.length > previousBookmarkData?.length;
          isBookmarkAdded && onBookmarkArticle(authors);
        }
        toast.success('تغییرات با موفقیت ذخیره شد');
      },
    },
  );
};

export const useArticleBookmarkStatus = ({
  articleId,
  enabled,
}: {
  articleId: string;
  enabled?: boolean;
}) => {
  const { user } = useAuth();

  return useQuery(
    ['bookmarkStatus', articleId],
    async () => {
      const result = await getArticleBookmarkStatus(articleId, user?.access_token as string);
      return result.data;
    },
    { enabled },
  );
};
